import { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  ArrowRight,
  Plus,
  User,
  Building2,
  FileText,
  LifeBuoy,
  Loader2,
} from 'lucide-react';
import { useCommandStore, type CommandItem } from '@/stores/commandStore';
import { api } from '@/lib/api';

// ---------------------------------------------------------------------------
// Types for search API response
// ---------------------------------------------------------------------------
interface SearchContact {
  id: string;
  name: string;
  email: string | null;
  type: string;
}
interface SearchCompany {
  id: string;
  name: string;
  domain: string | null;
  industry: string | null;
}
interface SearchInvoice {
  id: string;
  invoiceNumber: string;
  amount: number;
  currency: string;
  status: string;
}
interface SearchTicket {
  id: string;
  subject: string;
  status: string;
  priority: string;
}

interface SearchResults {
  contacts?: SearchContact[];
  companies?: SearchCompany[];
  invoices?: SearchInvoice[];
  tickets?: SearchTicket[];
}

interface SearchResponse {
  success: boolean;
  data: SearchResults;
  meta: { total: number };
}

// ---------------------------------------------------------------------------
// Presentable search result item
// ---------------------------------------------------------------------------
interface SearchResultItem {
  id: string;
  label: string;
  subtitle?: string;
  badge: string;
  icon: typeof User;
  path: string;
}

function mapSearchResults(data: SearchResults): SearchResultItem[] {
  const items: SearchResultItem[] = [];

  if (data.contacts) {
    for (const c of data.contacts) {
      items.push({
        id: `sr-contact-${c.id}`,
        label: c.name,
        subtitle: c.email ?? undefined,
        badge: 'Contact',
        icon: User,
        path: `/contacts/${c.id}`,
      });
    }
  }

  if (data.companies) {
    for (const c of data.companies) {
      items.push({
        id: `sr-company-${c.id}`,
        label: c.name,
        subtitle: c.domain ?? undefined,
        badge: 'Company',
        icon: Building2,
        path: `/contacts`, // companies view lives under contacts for now
      });
    }
  }

  if (data.invoices) {
    for (const inv of data.invoices) {
      items.push({
        id: `sr-invoice-${inv.id}`,
        label: inv.invoiceNumber,
        subtitle: `${inv.currency} ${inv.amount.toFixed(2)} — ${inv.status}`,
        badge: 'Invoice',
        icon: FileText,
        path: `/finance`,
      });
    }
  }

  if (data.tickets) {
    for (const t of data.tickets) {
      items.push({
        id: `sr-ticket-${t.id}`,
        label: t.subject,
        subtitle: `${t.priority} — ${t.status}`,
        badge: 'Ticket',
        icon: LifeBuoy,
        path: `/support`,
      });
    }
  }

  return items;
}

// ---------------------------------------------------------------------------
// Badge color helper
// ---------------------------------------------------------------------------
function badgeColor(badge: string): string {
  switch (badge) {
    case 'Contact':
      return 'bg-blue-500/20 text-blue-400';
    case 'Company':
      return 'bg-purple-500/20 text-purple-400';
    case 'Invoice':
      return 'bg-emerald-500/20 text-emerald-400';
    case 'Ticket':
      return 'bg-amber-500/20 text-amber-400';
    default:
      return 'bg-surface-600 text-surface-300';
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function CommandPalette() {
  const navigate = useNavigate();
  const { open, query, commands, toggle, setOpen, setQuery, register } =
    useCommandStore();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Register default commands
  useEffect(() => {
    const nav = (path: string) => () => {
      navigate(path);
    };

    const defaultCommands: CommandItem[] = [
      // Navigate section
      {
        id: 'nav-command-center',
        label: 'Command Center',
        section: 'Navigate',
        action: nav('/'),
        keywords: ['home', 'dashboard'],
      },
      {
        id: 'nav-contacts',
        label: 'Contacts',
        section: 'Navigate',
        action: nav('/contacts'),
        keywords: ['crm', 'people'],
      },
      {
        id: 'nav-email',
        label: 'Email Ops',
        section: 'Navigate',
        action: nav('/email'),
        keywords: ['inbox', 'mail'],
      },
      {
        id: 'nav-support',
        label: 'Support Ops',
        section: 'Navigate',
        action: nav('/support'),
        keywords: ['tickets', 'help'],
      },
      {
        id: 'nav-finance',
        label: 'Finance Ops',
        section: 'Navigate',
        action: nav('/finance'),
        keywords: ['invoices', 'billing'],
      },
      {
        id: 'nav-partnerships',
        label: 'Partnerships',
        section: 'Navigate',
        action: nav('/partnerships'),
        keywords: ['partners'],
      },
      {
        id: 'nav-growth',
        label: 'Growth Engine',
        section: 'Navigate',
        action: nav('/growth'),
        keywords: ['signals', 'leads'],
      },
      {
        id: 'nav-messaging',
        label: 'Messaging Hub',
        section: 'Navigate',
        action: nav('/messaging'),
        keywords: ['sms', 'whatsapp', 'chat'],
      },
      {
        id: 'nav-voice',
        label: 'Voice Ops',
        section: 'Navigate',
        action: nav('/voice'),
        keywords: ['calls', 'phone'],
      },
      {
        id: 'nav-context',
        label: 'Context Explorer',
        section: 'Navigate',
        action: nav('/context'),
        keywords: ['graph', 'relationships'],
      },
      {
        id: 'nav-settings',
        label: 'Settings',
        section: 'Navigate',
        action: nav('/settings'),
        keywords: ['config', 'preferences'],
      },
      {
        id: 'nav-audit',
        label: 'Audit Log',
        section: 'Navigate',
        action: nav('/audit'),
        keywords: ['history', 'compliance'],
      },
      // Create section
      {
        id: 'create-contact',
        label: 'New Contact',
        section: 'Create',
        action: nav('/contacts'),
        keywords: ['add', 'person'],
      },
      {
        id: 'create-ticket',
        label: 'New Ticket',
        section: 'Create',
        action: nav('/support'),
        keywords: ['add', 'ticket'],
      },
      {
        id: 'create-invoice',
        label: 'New Invoice',
        section: 'Create',
        action: nav('/finance'),
        keywords: ['add', 'invoice'],
      },
      {
        id: 'create-email',
        label: 'Compose Email',
        section: 'Create',
        action: nav('/email'),
        keywords: ['send', 'email', 'write'],
      },
    ];

    register(defaultCommands);
  }, [navigate, register]);

  // Filter commands by query
  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands.filter((cmd) => {
      if (cmd.label.toLowerCase().includes(q)) return true;
      if (cmd.section.toLowerCase().includes(q)) return true;
      if (cmd.description?.toLowerCase().includes(q)) return true;
      if (cmd.keywords?.some((k) => k.toLowerCase().includes(q))) return true;
      return false;
    });
  }, [commands, query]);

  // Group by section
  const grouped = useMemo(() => {
    const map = new Map<string, CommandItem[]>();
    for (const cmd of filtered) {
      const items = map.get(cmd.section) || [];
      items.push(cmd);
      map.set(cmd.section, items);
    }
    return map;
  }, [filtered]);

  // Flat list for keyboard navigation (commands + search results)
  const flatItems = useMemo(() => {
    const items: Array<{ type: 'command'; item: CommandItem } | { type: 'search'; item: SearchResultItem }> = [];
    for (const group of grouped.values()) {
      for (const cmd of group) {
        items.push({ type: 'command', item: cmd });
      }
    }
    for (const sr of searchResults) {
      items.push({ type: 'search', item: sr });
    }
    return items;
  }, [grouped, searchResults]);

  // Debounced search when query has 2+ chars
  useEffect(() => {
    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    // Abort previous request
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await api.get<SearchResponse>(
          `/api/search?q=${encodeURIComponent(trimmed)}`,
          { signal: controller.signal },
        );
        if (res.success) {
          setSearchResults(mapSearchResults(res.data));
        } else {
          setSearchResults([]);
        }
      } catch {
        // Aborted or network error — ignore
        if (!controller.signal.aborted) {
          setSearchResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setSearching(false);
        }
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (abortRef.current) {
        abortRef.current.abort();
      }
    };
  }, [query]);

  // Clear search results when palette closes
  useEffect(() => {
    if (!open) {
      setSearchResults([]);
      setSearching(false);
    }
  }, [open]);

  // Reset selection when query changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      // Small delay to ensure the DOM has rendered
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [open]);

  // Global Ctrl+K / Cmd+K listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [toggle]);

  // Scroll selected item into view
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector('[data-selected="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex, open]);

  const executeItem = useCallback(
    (item: CommandItem) => {
      setOpen(false);
      item.action();
    },
    [setOpen],
  );

  const navigateToResult = useCallback(
    (item: SearchResultItem) => {
      setOpen(false);
      navigate(item.path);
    },
    [setOpen, navigate],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((i) => (i + 1) % Math.max(flatItems.length, 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((i) =>
          i <= 0 ? Math.max(flatItems.length - 1, 0) : i - 1,
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const current = flatItems[selectedIndex];
        if (current) {
          if (current.type === 'command') {
            executeItem(current.item);
          } else {
            navigateToResult(current.item);
          }
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
      }
    },
    [flatItems, selectedIndex, executeItem, navigateToResult, setOpen],
  );

  if (!open) return null;

  let itemIndex = -1;

  return (
    <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[20vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />

      {/* Dialog */}
      <div
        className="relative w-full max-w-xl rounded-2xl border border-surface-600 bg-surface-800 shadow-2xl overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-surface-700 px-4 py-3">
          <Search className="h-4 w-4 text-surface-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search or jump to..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="flex-1 bg-transparent text-sm text-surface-100 placeholder-surface-500 outline-none"
          />
          {searching && (
            <Loader2 className="h-4 w-4 animate-spin text-surface-500 flex-shrink-0" />
          )}
        </div>

        {/* Results */}
        <div
          ref={listRef}
          className="max-h-80 overflow-y-auto overscroll-contain py-2"
        >
          {flatItems.length === 0 && !searching && (
            <div className="px-4 py-8 text-center text-sm text-surface-500">
              No results found
            </div>
          )}

          {flatItems.length === 0 && searching && (
            <div className="px-4 py-8 text-center text-sm text-surface-500">
              Searching...
            </div>
          )}

          {/* Command sections */}
          {Array.from(grouped.entries()).map(([section, items]) => (
            <div key={section}>
              <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-surface-500">
                {section}
              </div>
              {items.map((item) => {
                itemIndex++;
                const idx = itemIndex;
                const isSelected = idx === selectedIndex;
                const SectionIcon =
                  section === 'Create' ? Plus : ArrowRight;

                return (
                  <button
                    key={item.id}
                    data-selected={isSelected}
                    onClick={() => executeItem(item)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${
                      isSelected
                        ? 'bg-surface-700 text-surface-100'
                        : 'text-surface-300 hover:bg-surface-700/50'
                    }`}
                  >
                    <SectionIcon className="h-3.5 w-3.5 flex-shrink-0 text-surface-500" />
                    <span className="flex-1">{item.label}</span>
                    {item.description && (
                      <span className="text-xs text-surface-500">
                        {item.description}
                      </span>
                    )}
                    {isSelected && (
                      <span className="text-[10px] text-surface-500">
                        ↵
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}

          {/* Search results section */}
          {searchResults.length > 0 && (
            <div>
              <div className="px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-surface-500">
                Search Results
              </div>
              {searchResults.map((result) => {
                itemIndex++;
                const idx = itemIndex;
                const isSelected = idx === selectedIndex;
                const Icon = result.icon;

                return (
                  <button
                    key={result.id}
                    data-selected={isSelected}
                    onClick={() => navigateToResult(result)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${
                      isSelected
                        ? 'bg-surface-700 text-surface-100'
                        : 'text-surface-300 hover:bg-surface-700/50'
                    }`}
                  >
                    <Icon className="h-3.5 w-3.5 flex-shrink-0 text-surface-500" />
                    <span className="flex-1 truncate">{result.label}</span>
                    {result.subtitle && (
                      <span className="text-xs text-surface-500 truncate max-w-[140px]">
                        {result.subtitle}
                      </span>
                    )}
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${badgeColor(result.badge)}`}
                    >
                      {result.badge}
                    </span>
                    {isSelected && (
                      <span className="text-[10px] text-surface-500">
                        ↵
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer hint bar */}
        <div className="flex items-center gap-4 border-t border-surface-700 px-4 py-2 text-[10px] text-surface-500">
          <span className="flex items-center gap-1">
            <kbd className="rounded bg-surface-700 px-1 py-0.5 font-mono">
              ↑↓
            </kbd>
            navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded bg-surface-700 px-1 py-0.5 font-mono">
              ↵
            </kbd>
            select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="rounded bg-surface-700 px-1 py-0.5 font-mono">
              esc
            </kbd>
            close
          </span>
        </div>
      </div>
    </div>
  );
}
